import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import Slider from './Slider.jsx';
import '../../../../stylesheets/components/location_history_playback_controls.scss';

import LowerActionPanel from './LowerActionPanel.jsx';

// How many seconds back/forward should a single click for rewind/forward take us
const PLAYBACK_JUMP_INTERVAL = 5 * 60;

class LocationHistoryPlaybackControls extends Component {
  mode = 'pause';

  playbackSpeed = 1;

  refTime = null;

  startedPlayingAt = null;

  playTimer = null;

  // Use an instance field to store the displayTime (vs React state) because when
  // when we manipulate this variable we need it to be instant (no async state
  // updates).
  displayTime = this.props.startTime;

  state = {
    displayTime: this.props.startTime,
    mode: this.mode,
    playbackSpeed: this.playbackSpeed,
    sliderValue: 0,
  }

  reset = () => {
    this.mode = 'pause';
    this.playbackSpeed = 1;
    this.refTime = null;
    this.staredPlayingAt = null;
    this.displayTime = this.props.startTime;

    this.setState({
      displayTime: this.props.startTime,
      mode: this.mode,
      playbackSpeed: this.playbackSpeed,
      sliderValue: 0,
    });

    clearInterval(this.playTimer);
  }

  componentWillUnmount() {
    this.stop();
  }

  componentDidUpdate(prevProps, prevState) {
    // If caller changes input then reset the state of everything
    if (this.props.startTime !== prevProps.startTime
      || this.props.endTime !== prevProps.endTime) {
      this.reset();
    }
  }

  /**
   * Updates the display time.
   */
  updateDisplayTime = (displayTime) => {
    this.displayTime = displayTime;
    this.setState({
      displayTime,
      sliderValue: this.convertTimeToSliderValue(displayTime),
    });
    this.props.onTimeChange(displayTime);
  }

  /**
   * Force sets the display time and resets the live play timer if necessary.
   */
  setDisplayTime = (displayTime) => {
    this.displayTime = displayTime;
    this.resetPlayTimer();
    this.updateDisplayTime(displayTime);
  }

  onSliderValueChange = (sliderValue) => {
    // If slider value changed as the result of slider move, then adjust the
    // display time accordingly
    if (sliderValue !== this.state.sliderValue) {
      const newDisplayTime = this.convertSliderValueToTime(sliderValue);
      this.setDisplayTime(newDisplayTime);
    }
  }

  convertSliderValueToTime = (sliderValue) => {
    const { startTime, endTime } = this.props;
    const period = endTime.valueOf() - startTime.valueOf();
    return moment(startTime.valueOf() + (period * sliderValue));
  }

  convertTimeToSliderValue = (t) => {
    const { startTime, endTime } = this.props;
    const period = endTime.valueOf() - startTime.valueOf();
    return (t - startTime) / period;
  }

  formattedDisplayDate = () => moment(this.displayTime).format('ddd MMM Do');

  formattedDisplayTime = () => moment(this.displayTime).format('h:mm:ss A');

  resetPlayTimer = () => {
    this.baseTime = this.displayTime;
    this.timerStart = moment();
  }

  play = () => {
    this.resetPlayTimer();

    this.playTimer = setInterval(() => {
      const timePlaying = moment() - this.timerStart;
      const newDisplayTime = this.baseTime + ((this.state.playbackSpeed ** 5) * timePlaying);

      if (newDisplayTime > this.props.endTime) {
        this.togglePlayMode();
        return;
      }

      this.updateDisplayTime(newDisplayTime);
    }, 250);

    this.setState({
      mode: 'play',
    });
  }

  stop = () => {
    if (this.playTimer) {
      clearInterval(this.playTimer);
    }

    this.setState({
      mode: 'stop',
    });
  }

  togglePlayMode = () => {
    this.mode = this.mode === 'play' ? 'pause' : 'play';

    if (this.mode === 'play') {
      this.play();
    } else {
      this.stop();
    }
  }

  moveForward = (seconds) => {
    let newTime = this.state.displayTime + seconds * 1000;
    if (newTime > this.props.endTime) {
      newTime = this.props.endTime;
    }

    this.setDisplayTime(newTime);
  }

  moveBackward = (seconds) => {
    let newTime = this.state.displayTime - seconds * 1000;
    if (newTime < this.props.startTime) {
      newTime = this.props.startTime;
    }
    this.setDisplayTime(newTime);
  }

  adjustPlaybackSpeed = () => {
    this.playbackSpeed = this.playbackSpeed + 1;
    if (this.playbackSpeed > 4) {
      this.playbackSpeed = 1;
    }

    this.resetPlayTimer();
    this.setState({
      playbackSpeed: this.playbackSpeed,
    });
  }

  shutdownComponent = () => {
    const { onClose } = this.props;
    this.stop();
    onClose();
  }

  render() {
    const { open } = this.props;

    return (
      <LowerActionPanel open={open} onClose={this.shutdownComponent}>
        <div className="playback-window-panel">
          <div className="playback-timer-row">
            <div className="playback-timer__date">{this.formattedDisplayDate()}</div>
            <div className="playback-timer__time">{this.formattedDisplayTime()}</div>
            <div className="playback-timer__speed">
            </div>
          </div>
          <div className="playback-slider-row">
            <Slider
              value={this.state.sliderValue}
              onChange={this.onSliderValueChange}/>
          </div>
          <div className="playback-buttons-row">
            <div className="playback-buttons__back">
              <button className="controlBtn" onClick={() => this.moveBackward(PLAYBACK_JUMP_INTERVAL)}>
                <i className="shieldgps-icon icon-back-5-bold"></i>
              </button>
            </div>
            <div className="playback-buttons__play">
              <button className="controlBtn" onClick={this.togglePlayMode}>
                {this.state.mode === 'play' ? (
                  <i className="shieldgps-icon icon-pause-filled"></i>
                ) : (
                  <i className="shieldgps-icon icon-play-filled"></i>
                )}
              </button>
              <button className="playback-buttons__playbackSpeedBtn" onClick={this.adjustPlaybackSpeed}>{this.state.playbackSpeed}x</button>
            </div>
            <div className="playback-buttons__forward">
              <button className="controlBtn" onClick={() => this.moveForward(PLAYBACK_JUMP_INTERVAL)}>
                <i className="shieldgps-icon icon-forward-5-bold"></i>
              </button>
            </div>
          </div>
        </div>
      </LowerActionPanel>
    );
  }
}

LocationHistoryPlaybackControls.propTypes = {
  open: PropTypes.bool,
  startTime: PropTypes.object,
  endTime: PropTypes.object,
  onTimeChange: PropTypes.func,
  onClose: PropTypes.func,
};

export default LocationHistoryPlaybackControls;
